import { useParams, Link } from "react-router-dom";
import { useMemo, useEffect, useState } from "react";
import { Clock, Calendar, Tag, ArrowRight } from "lucide-react";
import { getArticle, getCategory, related, formatDate, SITE } from "@/lib/site";
import { AdSlot } from "@/components/AdSlot";
import { Newsletter } from "@/components/Newsletter";
import { ArticleCard } from "@/components/ArticleCard";
import { useSEO } from "@/lib/useSEO";

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const [active, setActive] = useState("");
  const cat0 = article ? getCategory(article.categorySlug) : null;

  useSEO({
    title: article ? article.seoTitle : "Artículo",
    description: article?.metaDescription,
    canonical: article ? `${SITE.url}/articulo/${article.slug}` : undefined,
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.metaDescription,
          image: [article.image],
          datePublished: article.date,
          dateModified: article.date,
          author: { "@type": "Organization", name: article.author },
          publisher: {
            "@type": "Organization",
            name: SITE.name,
            url: SITE.url,
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.url}/articulo/${article.slug}` },
          articleSection: cat0?.name,
          keywords: article.keyword,
        }
      : undefined,
  });

  const { html, toc } = useMemo(() => {
    if (!article) return { html: "", toc: [] };
    let raw = article.contentHtml || "";
    const items = [];
    raw = raw.replace(/<h2(.*?)>(.*?)<\/h2>/gi, (m, attrs, text) => {
      const id = text.replace(/<[^>]+>/g, "").trim().toLowerCase().replace(/[^a-z0-9áéíóúñü\s]/gi, "").replace(/\s+/g, "-");
      items.push({ id, text: text.replace(/<[^>]+>/g, "") });
      return `<h2 id="${id}"${attrs}>${text}</h2>`;
    });
    return { html: raw, toc: items };
  }, [article]);

  // Inject ad after 3rd paragraph
  const htmlWithAd = useMemo(() => {
    if (!html) return "";
    let count = 0;
    return html.replace(/<\/p>/gi, (m) => {
      count += 1;
      if (count === 3) return '</p><div data-inarticle-ad="1"></div>';
      return m;
    });
  }, [html]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-100px 0px -70% 0px" }
    );
    document.querySelectorAll(".prose-article h2").forEach((h) => obs.observe(h));
    return () => obs.disconnect();
  }, [htmlWithAd]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-4xl font-semibold">Artículo no encontrado</h1>
        <Link to="/" className="text-brand-terra mt-4 inline-block">Volver al inicio</Link>
      </div>
    );
  }

  const cat = getCategory(article.categorySlug);
  const rel = related(article);
  const parts = htmlWithAd.split('<div data-inarticle-ad="1"></div>');

  return (
    <article className="pb-10">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-12">
        <nav className="text-sm text-brand-sub mb-5">
          <Link to="/" className="hover:text-brand-terra">Inicio</Link> <span className="mx-1">/</span>{" "}
          <Link to={`/categoria/${cat?.slug}`} className="hover:text-brand-terra">{cat?.name}</Link>
        </nav>
        <span className="text-xs uppercase tracking-[0.2em] text-brand-terra">{cat?.name}</span>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold leading-tight text-brand-ink mt-3">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-brand-sub">
          <span className="flex items-center gap-1.5"><Calendar size={15} /> {formatDate(article.date)}</span>
          <span className="flex items-center gap-1.5"><Clock size={15} /> {article.readingTime} min</span>
          <span className="flex items-center gap-1.5"><Tag size={15} /> {article.keyword}</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8">
        <div className="relative overflow-hidden rounded-lg aspect-[16/9]">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Body + sidebar */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12">
        <div>
          <div className="prose-article" dangerouslySetInnerHTML={{ __html: parts[0] }} />
          {parts.length > 1 && (
            <>
              <AdSlot />
              <div className="prose-article" dangerouslySetInnerHTML={{ __html: parts.slice(1).join("") }} />
            </>
          )}

          {/* CTA */}
          <div className="bg-brand-adbg border-l-4 border-brand-terra rounded-r-md p-6 my-10">
            <p className="font-heading text-2xl font-semibold text-brand-ink mb-2">¿Te ha sido útil?</p>
            <p className="text-brand-sub mb-4">{article.cta}</p>
            <Newsletter variant="footer" />
          </div>

          <AdSlot label="Publicidad" />
        </div>

        {/* Sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-6">
            {toc.length > 0 && (
              <div className="bg-brand-surface border border-brand-line rounded-md p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-brand-sub mb-3">En este artículo</p>
                <ul className="space-y-2 text-sm">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a
                        href={`#${t.id}`}
                        className={`block transition-colors ${active === t.id ? "text-brand-terra font-semibold" : "text-brand-sub hover:text-brand-ink"}`}
                      >
                        {t.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <AdSlot label="Publicidad" height="h-[600px]" />
          </div>
        </aside>
      </div>

      {/* Related */}
      {rel.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-16">
          <h2 className="font-heading text-3xl font-semibold text-brand-ink mb-8">Sigue leyendo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {rel.map((a, i) => <ArticleCard key={a.slug} article={a} index={i} />)}
          </div>
        </section>
      )}
    </article>
  );
}
